import { useTheme } from '@emotion/react';
import { useState, useEffect, useRef } from 'react';
import { useId } from 'react-id-generator';
import { Label } from '../forms/Label/index.js';
import { Message } from '../forms/Message/index.js';
import { ScreenReaderOnly } from '../ScreenReaderOnly/index.js';
import { Text } from '../Text/index.js';
import { Dropzone } from './Dropzone.js';
import { File } from './File.js';
import { getFileUploaderCopy } from './i18n.js';
import { FileUploaderWrapper, EmptyZone } from './styles.js';
import { composeKey, isStoredFile, getReadableFileSize } from './utils.js';
import { jsxs, jsx } from '@emotion/react/jsx-runtime';

const FileUploader = ({
  accept,
  className,
  ['data-testid']: dataTestId,
  disabled,
  error,
  id,
  isMulti = false,
  label,
  labelDescription,
  maxFiles,
  maxSize,
  maxPolicySize,
  message,
  onFileChange,
  onRemove,
  readOnly,
  uploadFile,
  value,
  ...spacerOptions
}) => {
  const [uniqueId] = useId(1, 'fileUploader-');
  const [files, setFiles] = useState(() => !Array.isArray(value) ? value ? [{
    file: value,
    id: composeKey(value)
  }] : [] : value.map(val => ({
    file: val,
    id: composeKey(val)
  })));
  const [loadingCount, setLoadingCount] = useState(0);
  const [innerErrorMessage, setInnerErrorMessage] = useState('');
  const [reset, setReset] = useState(true);
  const inputId = id || uniqueId;
  const {
    translate
  } = useTheme();
  useEffect(() => {
    if (!reset) setReset(true);
  }, [reset]);
  useEffect(() => {
    if (onFileChange) {
      onFileChange(isMulti ? files.filter(item => isStoredFile(item.file)).map(({
        file
      }) => ({
        ...file
      })) : files.length && isStoredFile(files[0].file) ? {
        ...files[0].file
      } : null);
    }
  }, [files, isMulti]);
  const fileInputRef = useRef(null);
  const {
    EXISTS,
    MAX_FILES,
    NO_FILES,
    MAX_SIZE,
    MAX_POLICY_SIZE
  } = getFileUploaderCopy(translate);
  const LOADING = {
    INCREMENT: (num = 1) => setLoadingCount(previous => previous + num),
    DECREMENT: (num = 1) => setLoadingCount(previous => previous - num),
    IS_LOADING: () => loadingCount !== 0
  };
  const cleanErrorMessages = () => setInnerErrorMessage('');
  const internalDisabled = () => disabled || files.length >= maxFiles;
  const shouldShowDropzone = () => isMulti && !disabled && !readOnly || !files.length && !readOnly;
  const shouldShowEmptyState = () => readOnly && !files.length;
  const shouldShowErrorMessage = () => Boolean(innerErrorMessage) || Boolean(error) && !LOADING.IS_LOADING();
  const runUploadGuards = items => {
    const existsWithoutError = item => files.some(file => {
      return composeKey(file.file) === composeKey(item) && !file.error;
    });
    const exceedsMaxSize = item => item.size > maxSize;
    const exceedsMaxPolicySize = item => item.size > maxPolicySize;
    const exceedsMaxFiles = index => maxFiles && index + files.length >= maxFiles;
    const guardedFiles = items.map((item, index) => {
      let error;
      if (exceedsMaxSize(item)) error = MAX_SIZE(getReadableFileSize(maxSize));
      if (exceedsMaxPolicySize(item)) error = MAX_POLICY_SIZE;
      if (exceedsMaxFiles(index)) error = MAX_FILES(maxFiles);
      if (existsWithoutError(item)) error = EXISTS(item.name);
      return {
        file: item,
        error,
        id: error ? uniqueId : composeKey(item)
      };
    });
    const filesToUpload = guardedFiles.filter(({
      error
    }) => !error);
    LOADING.INCREMENT(filesToUpload.length);
    return guardedFiles;
  };
  const handleClick = () => fileInputRef.current.click();
  const handleRemove = id => {
    cleanErrorMessages();
    const removedFile = files.find(item => id !== item.id);
    setFiles(previous => {
      return isMulti ? previous.filter(item => id !== item.id) : [];
    });
    if (onRemove) onRemove(removedFile.file);
  };
  const mergeFiles = fileList => {
    const values = [...fileList];
    const guardedFiles = runUploadGuards(values);
    setFiles(previous => isMulti ? [...previous, ...guardedFiles] : guardedFiles);
    setReset(false);
  };
  const handleDrop = event => {
    event.persist();
    const droppedFiles = event.dataTransfer.files;
    mergeFiles(droppedFiles);
  };
  const handleChange = event => {
    event.persist();
    const changedFiles = event.target.files;
    mergeFiles(changedFiles);
  };
  const handleOnUpload = (storedFile, id) => {
    LOADING.DECREMENT();
    if (storedFile) {
      const fileUploaderfile = {
        file: storedFile,
        id: composeKey(storedFile)
      };
      setFiles(previous => isMulti ? [...previous.filter(item => id !== item.id), fileUploaderfile] : [fileUploaderfile]);
    }
  };
  return jsxs(FileUploaderWrapper, {
    id: id,
    className: className,
    ...spacerOptions,
    children: [jsx(Label, {
      disabled: disabled,
      htmlFor: inputId,
      mb: 1,
      children: label
    }), message && jsx(Message, {
      size: "xs",
      disabled: disabled,
      message: message
    }), shouldShowDropzone() && jsx(Dropzone, {
      "data-testid": dataTestId,
      disabled: disabled || files.length >= maxFiles,
      error: !internalDisabled() && (Boolean(error) || Boolean(innerErrorMessage)) && !LOADING.IS_LOADING(),
      onClick: !(disabled || readOnly) && handleClick,
      onDrop: !(disabled || readOnly) && handleDrop
    }), shouldShowEmptyState() && jsx(EmptyZone, {
      children: jsx(Text.Body, {
        children: NO_FILES
      })
    }), shouldShowErrorMessage() && jsx(Message, {
      disabled: disabled,
      error: innerErrorMessage || error,
      "data-testid": `${dataTestId}-error-text`
    }), isMulti ? Boolean(files.length) && files.sort((a, b) => {
      if (a.error) return 1;
      if (b.error) return -1;
      return a.file.name.localeCompare(b.file.name);
    }).map(item => {
      return jsx(File, {
        "data-testid": `${dataTestId}-file`,
        error: item.error,
        file: item.file,
        maxSize: maxSize,
        mt: 2,
        onRemove: () => handleRemove(item.id),
        onUpload: storedFile => handleOnUpload(storedFile, item.id),
        uploadFile: uploadFile,
        readOnly: readOnly || disabled
      }, item.id);
    }) : Boolean(files.length) && jsx(File, {
      "data-testid": `${dataTestId}-file`,
      error: files[0].error,
      file: files[0].file,
      maxSize: maxSize,
      onRemove: handleRemove,
      uploadFile: uploadFile,
      onUpload: handleOnUpload,
      readOnly: readOnly || disabled
    }), jsx("label", {
      "aria-label": labelDescription,
      children: reset && jsx(ScreenReaderOnly, {
        children: jsx("input", {
          id: inputId,
          disabled: disabled,
          multiple: isMulti,
          onChange: ev => {
            handleChange(ev);
          },
          accept: accept,
          ref: fileInputRef,
          readOnly: readOnly,
          type: "file",
          style: {
            display: 'none'
          }
        })
      })
    })]
  });
};

export { FileUploader };
